

import React, { useState } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { API_BASE_URL } from "../../../../config";

const AddProjectForm = ({ onClose, onSuccess }) => {

  const fieldMap = {
    "Project Name*": "projectName",
    "Area": "area",
    "Project Address*": "projectAddress",
    "Electricity Section": "electricitySection",
    "Property Type*": "propertyType",
    "Front Road Size": "frontRoadSize",
    "Water Supply": "waterSupply",
    "Property Status": "propertyStatus",
    "Hand Over": "handOver",
    "No of Tower": "noOfTower",
    "Project Type": "projectType",
    "Building Floors": "buildingFloors",
    "Property on floor number": "propertyOnFloorNumber",
    "Project  Contact name": "projectContactName",
    "Project Contact number": "projectContactNumber",
    "Flat Type": "flatType",
    "Flat Facing": "flatFacing",
    "Super Buildup": "superBuiltUp",
    "Carpet Area": "carpetArea",
    "Deduction %": "deductionPercentage",
    "Rate per Sqf": "ratePerSqft",
    "Flat Price": "flatPrice",
    "No of Flat Available": "noOfFlatAvailable",
    "Approved Bank": "approvedBank",
    "Parking Type": "parkingType",
    "Parking Rate": "parkingRate",
    "Amenities Charge": "amenitiesCharge",
    "Maintenance Charge": "maintenanceCharge",
    "Any Others charges": "anyOtherCharges",
    "Amenities": "amenities",
    "Main Road Name": "mainRoadName",
    "Distance From Main road to Property": "distanceFromMainRoad",
    "Nearest Station Name": "nearestStationName",
    "Distance from Railway station": "distanceFromRailwayStation",
    "Nearest Bus Stop Name": "nearestBusStopName",
    "Distance from Bus Stand": "distanceFromBusStop",
    "Distance from Local market": "distanceFromLocalMarket",
    "Remarks": "remarks",
    "Photos or Video Available": "photosOrVideos",
    "Visited Status": "visitedStatus",
  };

  const dropdownOptions = {
    propertyType: ["Rent", "Residential", "Commercial", "Mixed Use"],
    propertyStatus: ["Ready to Move", "Under Construction"],
    handOver: ["Yes", "No"],
    flatType: ["1BHK", "2BHK", "3BHK", "4BHK", "Studio"],
    flatFacing: ["North-East", "South-East", "West-South", "West-North"],
    parkingType: ["Open", "Covered", "Stilt", "Basement"],
    projectType: ["Apartment", "Villa", "Plot", "Row House","Complex"],
    waterSupply: ["Municipal", "Borewell", "Both"],
    electricitySection: ["WBSE DCL", "CESC"],
    photosOrVideos: ["Yes", "No"],
    visitedStatus: ["Yes", "No"],
  };

  const [formData, setFormData] = useState(
    Object.fromEntries(Object.values(fieldMap).map((key) => [key, ""]))
  );

  const numericFields = [
    "deductionPercentage",
    "flatPrice",
    "parkingRate",
    "amenitiesCharge",
    "maintenanceCharge",
    "anyOtherCharges",
    "noOfFlatAvailable",
    "ratePerSqft",
    "superBuiltUp",
    "carpetArea",
  ];

  const validateField = (name, value) => {
    if (numericFields.includes(name)) {
      // only numbers allowed (decimals too)
      if (!/^[0-9]*\.?[0-9]*$/.test(value)) {
        return "Special characters not allowed";
      }
      // negative not allowed
      if (value !== "" && parseFloat(value) < 0) {
        return "Negative values not allowed";
      }
    }

    if (name === "noOfFlatAvailable") {
      if (!/^[0-9]*$/.test(value)) {
        return "Please enter a valid number";
      }
      if (value !== "" && parseInt(value, 10) < 0) {
        return "Negative values not allowed";
      }
    }

    if (name === "projectContactNumber") {
      // allow only numbers, slash, spaces
      if (!/^[0-9\s/]*$/.test(value)) {
        return "Only numbers and '/' allowed";
      }
    }

    return null;
  };

  const calculateValues = (updatedData) => {
    const { superBuiltUp, deductionPercentage, ratePerSqft } = updatedData;

    let calculatedCarpetArea = "";
    let calculatedFlatPrice = "";

    const sb = parseFloat(superBuiltUp);
    const dp = parseFloat(deductionPercentage);
    const rate = parseFloat(ratePerSqft);

    if (!isNaN(sb) && !isNaN(dp)) {
      calculatedCarpetArea = sb - (sb * dp) / 100;
    }

    if (!isNaN(sb) && !isNaN(rate)) {
      calculatedFlatPrice = sb * rate;
    }

    return {
      ...updatedData,
      carpetArea: calculatedCarpetArea || "",
      flatPrice: calculatedFlatPrice || "",
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const errorMessage = validateField(name, value);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    setFormData((prev) => {
      const updatedData = { ...prev, [name]: value };
      if (["superBuiltUp", "deductionPercentage", "ratePerSqft"].includes(name)) {
        return calculateValues(updatedData);
      }
      return updatedData;
    });

    // setFormData((prev) => ({
    //   ...prev,
    //   [name]: value,
    // }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("token");

    const loadingToast = toast.loading("Saving project...");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/project/add-project`,
        formData,
        {
          headers: {
            token,
          },
        }
      );

      toast.dismiss(loadingToast);

      if (response.data.success) {
        toast.success("Project added successfully!");
        if (onSuccess) onSuccess();
        setTimeout(onClose, 1500);
      } else {
        toast.error("Error: " + response.data.message);
      }
    } catch (err) {
      toast.dismiss(loadingToast);
      console.error(err);
      toast.error("Failed to add project.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-scroll bg-white p-6 dark:bg-navy-800">
      <Toaster position="top-right" reverseOrder={false} />
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">Add New Project</h2>
        <button className="text-red-600 hover:underline" onClick={onClose}>
          Close
        </button>
      </div>

      {/* <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">
          Add New Project
        </h2>
        <button
          className="rounded bg-red-700 px-4 py-2 text-white transition hover:bg-red-800"
          onClick={onClose}
        ></button>

      </div> */}
      <form
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3"
        onSubmit={handleSubmit}
      >
        {Object.keys(fieldMap).map((label, idx) => {
          const name = fieldMap[label];
          const options = dropdownOptions[name];

          return (
            <div key={idx} className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-600 dark:text-white">
                {label}
              </label>
              {name === "handOver" ? (
                <input
                  type="month"
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  className="rounded border border-gray-300 px-3 py-2 text-sm outline-none dark:bg-navy-700 dark:text-white"
                />
              ) : options ? (
                <select
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  className="rounded border border-gray-300 px-3 py-2 text-sm outline-none dark:bg-navy-700 dark:text-white"
                >
                  <option value="">Select {label}</option>
                  {options.map((option, i) => (
                    <option key={i} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  name={name}
                  value={formData[name]}
                  onChange={handleChange}
                  placeholder={`Enter ${label}`}
                  readOnly={["carpetArea", "flatPrice"].includes(name)}
                  className="rounded border border-gray-300 px-3 py-2 text-sm outline-none dark:bg-navy-700 dark:text-white"
                />
              )}
            </div>
          );
        })}

        <div className="col-span-full mt-4">
          <button
            type="submit"
            className="rounded bg-indigo-600 px-6 py-2 text-white hover:bg-indigo-700"
          >
            Save Project
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddProjectForm;

